import React from 'react';

import { v4 as uuid4 } from 'uuid';

import {
  colorBaseNeutral900,
  colorBaseNeutralWhite,
} from '@duxton-web/design-tokens/themes/light';
import { CloseUniversal } from '@duxton-web/icons';
import { Flex, message, MessageArgsProps } from '@duxton-web/ui';

import './SnackBar.scss';

interface Props extends MessageArgsProps {
  isClosable?: boolean;
  duration?: number;
  position?: 'left' | 'right' | 'center';
}

message.config({
  maxCount: 3, // Max message show, drop oldest if exceed limit
});

const SnackBar = ({
  type,
  content,
  handleClose,
}: Pick<MessageArgsProps, 'type' | 'content'> & {
  handleClose: () => void;
}) => {
  return (
    <Flex
      gap={5} align="center"
      style={{
        maxWidth: 316,
        textAlign: 'left',
      }}
    >
      {content}
      <CloseUniversal
        style={{
          fontSize: 16,
          color:
            type === 'warning' ? colorBaseNeutral900 : colorBaseNeutralWhite,
        }}
        onClick={() => handleClose()}
      />
    </Flex>
  );
};

const createSnackBar = ({
  type,
  content,
  duration,
  position,
  isClosable,
  ...props
}: Props) => {
  const key = uuid4();

  const handleClose = () => {
    message.destroy(key);
  };

  return message[type]({
    ...props,
    key,
    duration: duration || 5,
    style: {
      textAlign: position || 'right',
      ...props.style,
    },
    content: (
      <SnackBar type={type} content={content} handleClose={handleClose} />
    ),
  });
};

export const snackBar = {
  success: (props: Omit<Props, 'type'>) =>
    createSnackBar({ type: 'success', ...props }),
  info: (props: Omit<Props, 'type'>) =>
    createSnackBar({ type: 'info', ...props }),
  error: (props: Omit<Props, 'type'>) =>
    createSnackBar({ type: 'error', ...props }),
  warning: (props: Omit<Props, 'type'>) =>
    createSnackBar({ type: 'warning', ...props }),
  neutral: (props: Omit<Props, 'type'>) =>
    createSnackBar({ type: 'neutral', ...props }),
};
